import { inject, Injectable } from '@angular/core';
import { AppointmentStatus, AppointmentType } from '../models/appointment.model';
import { CONTACT_OUTCOME_KINDS, OutcomeKind } from '../models/interaction.model';
import { computeGrandTotal, formatLineItemsSummary, Lead, parseLeadServices } from '../models/lead.model';
import {
  AppointmentScheduleRow,
  DailyActivityRow,
  FunnelStage,
  LeadExportBundle,
  LeadSummaryRow,
  LeaderboardEntry,
  LeaderboardGroupBy,
  LeaderboardMetric,
  ObjectionBreakdownRow,
  RateTrendPoint,
  ReportBucket,
  ReportFilters,
  ReportGroupBy,
} from '../models/report.model';
import { AddressService } from './address.service';
import { AttachmentService } from './attachment.service';
import { AuthService } from './auth.service';
import { DatabaseService } from './database.service';
import { ObjectionService } from './objection.service';
import { TagService } from './tag.service';
import { TeamService } from './team.service';
import { UserService } from './user.service';

/** Interaction outcomes that produced (or nearly produced) a lead -- the "leads" metric
 *  across every report uses this single proxy rather than joining against the `leads`
 *  table separately in each bucketed/grouped query, so every report agrees with every
 *  other one. It slightly over-counts vs. the `leads` table itself (a canvasser can hit
 *  "Skip for now" on the lead-capture panel for a 'lead'-outcome interaction) -- an
 *  accepted simplification, not a source-of-truth swap. */
const LEAD_PRODUCING_OUTCOMES: readonly OutcomeKind[] = ['lead'];

interface ScopeClause {
  sql: string;
  params: unknown[];
}

/**
 * All report queries (design doc §8) run locally against SQLite -- no backend endpoint.
 * Every method takes `includeAllUsers` (computed by the caller from
 * `permissionService.has('interaction.read-others')`, the same permission that already
 * gates cross-user visibility everywhere else in this app) rather than deciding
 * visibility itself, so the scoping rule stays in exactly one place per call site,
 * matching the existing InteractionService.getForAddress(onlyUserId) pattern.
 */
@Injectable({ providedIn: 'root' })
export class ReportsService {
  private db = inject(DatabaseService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private teamService = inject(TeamService);
  private tagService = inject(TagService);
  private objectionService = inject(ObjectionService);
  private addressService = inject(AddressService);
  private attachmentService = inject(AttachmentService);

  async getDailyActivity(filters: ReportFilters, includeAllUsers: boolean): Promise<DailyActivityRow[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'created_by');
    const tag = this.tagClause(filters.tagId, 'interaction_tags', 'interaction_id', 'i.id');
    const outcome = this.outcomeClause(filters.outcomeKind, 'i.outcome_kind');
    const contactPlaceholders = CONTACT_OUTCOME_KINDS.map(() => '?').join(', ');
    const leadPlaceholders = LEAD_PRODUCING_OUTCOMES.map(() => '?').join(', ');

    const rows = (await this.db.exec(
      `SELECT i.created_by AS user_id,
              COUNT(*) AS doors,
              SUM(CASE WHEN i.outcome_kind IN (${contactPlaceholders}) THEN 1 ELSE 0 END) AS contacts,
              SUM(CASE WHEN i.outcome_kind IN (${leadPlaceholders}) THEN 1 ELSE 0 END) AS leads
       FROM interactions i
       WHERE i.deleted_at IS NULL AND i.occurred_at BETWEEN ? AND ?${scope.sql}${tag.sql}${outcome.sql}
       GROUP BY i.created_by`,
      [...CONTACT_OUTCOME_KINDS, ...LEAD_PRODUCING_OUTCOMES, filters.from, filters.to, ...scope.params, ...tag.params, ...outcome.params],
    )) as Array<{ user_id: string; doors: number; contacts: number; leads: number }>;

    const nameById = await this.userNameMap();
    return rows.map((r) => ({
      userId: r.user_id,
      userName: nameById.get(r.user_id) ?? r.user_id,
      doors: r.doors,
      contacts: r.contacts,
      contactRate: r.doors > 0 ? r.contacts / r.doors : 0,
      leads: r.leads,
      conversionRate: r.contacts > 0 ? r.leads / r.contacts : 0,
    }));
  }

  async getRateTrends(
    filters: ReportFilters,
    includeAllUsers: boolean,
    bucket: ReportBucket = 'day',
    groupBy: ReportGroupBy = 'user',
  ): Promise<RateTrendPoint[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'i.created_by');
    const outcome = this.outcomeClause(filters.outcomeKind, 'i.outcome_kind');
    const bucketSql = this.bucketExpr(bucket, 'i.occurred_at');
    const contactPlaceholders = CONTACT_OUTCOME_KINDS.map(() => '?').join(', ');
    const leadPlaceholders = LEAD_PRODUCING_OUTCOMES.map(() => '?').join(', ');
    const aggSql = `COUNT(*) AS doors,
              SUM(CASE WHEN i.outcome_kind IN (${contactPlaceholders}) THEN 1 ELSE 0 END) AS contacts,
              SUM(CASE WHEN i.outcome_kind IN (${leadPlaceholders}) THEN 1 ELSE 0 END) AS leads`;
    const aggParams = [...CONTACT_OUTCOME_KINDS, ...LEAD_PRODUCING_OUTCOMES];

    if (groupBy === 'tag') {
      const rows = (await this.db.exec(
        `SELECT ${bucketSql} AS bucket, jt.tag_id AS group_key, ${aggSql}
         FROM interactions i
         JOIN interaction_tags jt ON jt.interaction_id = i.id AND jt.deleted_at IS NULL
         WHERE i.deleted_at IS NULL AND i.occurred_at BETWEEN ? AND ?${scope.sql}${outcome.sql}
         GROUP BY bucket, jt.tag_id
         ORDER BY bucket`,
        [...aggParams, filters.from, filters.to, ...scope.params, ...outcome.params],
      )) as Array<{ bucket: string; group_key: string; doors: number; contacts: number; leads: number }>;
      const tags = await this.tagService.getAll();
      const label = new Map(tags.map((t) => [t.id, t.label]));
      return rows.map((r) => this.toRateTrendPoint(r, label.get(r.group_key) ?? r.group_key));
    }

    const rows = (await this.db.exec(
      `SELECT ${bucketSql} AS bucket, i.created_by AS group_key, ${aggSql}
       FROM interactions i
       WHERE i.deleted_at IS NULL AND i.occurred_at BETWEEN ? AND ?${scope.sql}${outcome.sql}
       GROUP BY bucket, i.created_by
       ORDER BY bucket`,
      [...aggParams, filters.from, filters.to, ...scope.params, ...outcome.params],
    )) as Array<{ bucket: string; group_key: string; doors: number; contacts: number; leads: number }>;

    if (groupBy === 'user') {
      const nameById = await this.userNameMap();
      return rows.map((r) => this.toRateTrendPoint(r, nameById.get(r.group_key) ?? r.group_key));
    }

    // groupBy === 'team': fan each user's bucket row out to every team they belong to --
    // teams are a grouping, not a partition (a user can be on more than one team).
    const teamsByUser = await this.teamsByUserMap();
    const points: RateTrendPoint[] = [];
    for (const r of rows) {
      for (const team of teamsByUser.get(r.group_key) ?? []) {
        points.push(this.toRateTrendPoint({ ...r, group_key: team.id }, team.name));
      }
    }
    return points;
  }

  async getLeadSummary(
    filters: ReportFilters,
    includeAllUsers: boolean,
    bucket: ReportBucket = 'day',
    groupBy: ReportGroupBy = 'user',
  ): Promise<LeadSummaryRow[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'l.created_by');
    const bucketSql = this.bucketExpr(bucket, 'l.created_at');
    const aggSql = `COUNT(*) AS lead_count,
              COALESCE(SUM(l.estimated_value), 0) AS total_value,
              SUM(CASE WHEN l.stage = 'won' THEN 1 ELSE 0 END) AS won_count,
              SUM(CASE WHEN l.stage = 'lost' THEN 1 ELSE 0 END) AS lost_count`;

    if (groupBy === 'tag') {
      const rows = (await this.db.exec(
        `SELECT ${bucketSql} AS bucket, jt.tag_id AS group_key, ${aggSql}
         FROM leads l
         JOIN lead_tags jt ON jt.lead_id = l.id AND jt.deleted_at IS NULL
         WHERE l.deleted_at IS NULL AND l.created_at BETWEEN ? AND ?${scope.sql}
         GROUP BY bucket, jt.tag_id
         ORDER BY bucket`,
        [filters.from, filters.to, ...scope.params],
      )) as Array<LeadSummaryQueryRow>;
      const tags = await this.tagService.getAll();
      const label = new Map(tags.map((t) => [t.id, t.label]));
      return rows.map((r) => this.toLeadSummaryRow(r, label.get(r.group_key) ?? r.group_key));
    }

    const rows = (await this.db.exec(
      `SELECT ${bucketSql} AS bucket, l.created_by AS group_key, ${aggSql}
       FROM leads l
       WHERE l.deleted_at IS NULL AND l.created_at BETWEEN ? AND ?${scope.sql}
       GROUP BY bucket, l.created_by
       ORDER BY bucket`,
      [filters.from, filters.to, ...scope.params],
    )) as Array<LeadSummaryQueryRow>;

    if (groupBy === 'user') {
      const nameById = await this.userNameMap();
      return rows.map((r) => this.toLeadSummaryRow(r, nameById.get(r.group_key) ?? r.group_key));
    }

    const teamsByUser = await this.teamsByUserMap();
    const out: LeadSummaryRow[] = [];
    for (const r of rows) {
      for (const team of teamsByUser.get(r.group_key) ?? []) {
        out.push(this.toLeadSummaryRow({ ...r, group_key: team.id }, team.name));
      }
    }
    return out;
  }

  /** The most actionable report per §8: why we're losing, ranked. */
  async getObjectionBreakdown(filters: ReportFilters, includeAllUsers: boolean): Promise<ObjectionBreakdownRow[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'created_by');
    const tag = this.tagClause(filters.tagId, 'interaction_tags', 'interaction_id', 'id');

    const rows = (await this.db.exec(
      `SELECT objection_id, COUNT(*) AS count
       FROM interactions
       WHERE deleted_at IS NULL AND outcome_kind = 'objection' AND occurred_at BETWEEN ? AND ?${scope.sql}${tag.sql}
       GROUP BY objection_id
       ORDER BY count DESC`,
      [filters.from, filters.to, ...scope.params, ...tag.params],
    )) as Array<{ objection_id: string; count: number }>;

    const total = rows.reduce((sum, r) => sum + r.count, 0);
    const objections = await this.objectionService.getAll();
    const label = new Map(objections.map((o) => [o.id, o.label]));
    return rows.map((r) => ({
      objectionId: r.objection_id,
      objectionLabel: label.get(r.objection_id) ?? r.objection_id,
      count: r.count,
      percentOfTotal: total > 0 ? r.count / total : 0,
    }));
  }

  /** Doors -> contacts -> leads -> appointments -> won, over the filtered range. */
  async getFunnel(filters: ReportFilters, includeAllUsers: boolean): Promise<FunnelStage[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const interactionScope = this.scopeClause(userIds, 'created_by');
    const appointmentScope = this.scopeClause(userIds, 'assigned_to');
    const leadScope = this.scopeClause(userIds, 'created_by');
    const contactPlaceholders = CONTACT_OUTCOME_KINDS.map(() => '?').join(', ');
    const leadPlaceholders = LEAD_PRODUCING_OUTCOMES.map(() => '?').join(', ');

    const [doors, contacts, leads, appointments, won] = await Promise.all([
      this.countOne(
        `SELECT COUNT(*) AS n FROM interactions WHERE deleted_at IS NULL AND occurred_at BETWEEN ? AND ?${interactionScope.sql}`,
        [filters.from, filters.to, ...interactionScope.params],
      ),
      this.countOne(
        `SELECT COUNT(*) AS n FROM interactions WHERE deleted_at IS NULL AND outcome_kind IN (${contactPlaceholders}) AND occurred_at BETWEEN ? AND ?${interactionScope.sql}`,
        [...CONTACT_OUTCOME_KINDS, filters.from, filters.to, ...interactionScope.params],
      ),
      this.countOne(
        `SELECT COUNT(*) AS n FROM interactions WHERE deleted_at IS NULL AND outcome_kind IN (${leadPlaceholders}) AND occurred_at BETWEEN ? AND ?${interactionScope.sql}`,
        [...LEAD_PRODUCING_OUTCOMES, filters.from, filters.to, ...interactionScope.params],
      ),
      this.countOne(
        `SELECT COUNT(*) AS n FROM appointments WHERE deleted_at IS NULL AND scheduled_start BETWEEN ? AND ?${appointmentScope.sql}`,
        [filters.from, filters.to, ...appointmentScope.params],
      ),
      this.countOne(
        `SELECT COUNT(*) AS n FROM leads WHERE deleted_at IS NULL AND stage = 'won' AND created_at BETWEEN ? AND ?${leadScope.sql}`,
        [filters.from, filters.to, ...leadScope.params],
      ),
    ]);

    const counts: Array<{ stage: FunnelStage['stage']; count: number }> = [
      { stage: 'doors', count: doors },
      { stage: 'contacts', count: contacts },
      { stage: 'leads', count: leads },
      { stage: 'appointments', count: appointments },
      { stage: 'won', count: won },
    ];
    return counts.map((c, i) => ({
      stage: c.stage,
      count: c.count,
      percentOfPrevious: i === 0 ? null : counts[i - 1].count > 0 ? c.count / counts[i - 1].count : 0,
      percentOfTotal: doors > 0 ? c.count / doors : 0,
    }));
  }

  async getAppointmentSchedule(filters: ReportFilters, includeAllUsers: boolean): Promise<AppointmentScheduleRow[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'a.assigned_to');

    const rows = (await this.db.exec(
      `SELECT a.id, a.lead_id, a.address_id, a.assigned_to, a.appointment_type, a.status,
              a.scheduled_start, a.scheduled_end
       FROM appointments a
       WHERE a.deleted_at IS NULL AND a.scheduled_start BETWEEN ? AND ?${scope.sql}
       ORDER BY a.scheduled_start`,
      [filters.from, filters.to, ...scope.params],
    )) as Array<{
      id: string;
      lead_id: string;
      address_id: string;
      assigned_to: string;
      appointment_type: AppointmentType;
      status: AppointmentStatus;
      scheduled_start: number;
      scheduled_end: number;
    }>;
    if (rows.length === 0) return [];

    const nameById = await this.userNameMap();
    const leadIds = [...new Set(rows.map((r) => r.lead_id).filter(Boolean))];
    const addressIds = [...new Set(rows.map((r) => r.address_id).filter(Boolean))];
    const [leadContactById, addressLabelById] = await Promise.all([
      this.leadContactNameMap(leadIds),
      this.addressLabelMap(addressIds),
    ]);

    return rows.map((r) => ({
      appointmentId: r.id,
      scheduledStart: r.scheduled_start,
      scheduledEnd: r.scheduled_end,
      appointmentType: r.appointment_type,
      status: r.status,
      assigneeName: nameById.get(r.assigned_to) ?? r.assigned_to,
      leadContactName: leadContactById.get(r.lead_id) ?? '',
      addressLabel: addressLabelById.get(r.address_id) ?? '',
    }));
  }

  /** Full lead detail + resolved attachments, for the CSV/XLSX/zip export. */
  async getLeadExportBundles(filters: ReportFilters, includeAllUsers: boolean): Promise<LeadExportBundle[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const scope = this.scopeClause(userIds, 'created_by');

    const leads = (await this.db.exec(
      `SELECT * FROM leads WHERE deleted_at IS NULL AND created_at BETWEEN ? AND ?${scope.sql} ORDER BY created_at DESC`,
      [filters.from, filters.to, ...scope.params],
    )) as Lead[];

    const nameById = await this.userNameMap();
    const bundles: LeadExportBundle[] = [];
    for (const lead of leads) {
      const [address, attachments] = await Promise.all([
        lead.address_id ? this.addressService.getById(lead.address_id) : Promise.resolve(null),
        this.attachmentService.getResolvedFor('lead', lead.id),
      ]);
      const lineItems = parseLeadServices(lead);
      bundles.push({
        lead,
        address,
        lineItems,
        attachments,
        row: {
          Contact: lead.contact_name,
          Phone: lead.contact_phone,
          Email: lead.contact_email,
          Address: address?.formatted_address ?? '',
          Stage: lead.stage,
          'Assigned to': nameById.get(lead.assigned_to) ?? '',
          Services: formatLineItemsSummary(lineItems),
          'Estimated value': lead.estimated_value ?? computeGrandTotal(lineItems),
          Currency: lead.currency,
          Notes: lead.notes,
        },
      });
    }
    return bundles;
  }

  async getLeaderboard(
    filters: ReportFilters,
    includeAllUsers: boolean,
    metric: LeaderboardMetric,
    groupBy: LeaderboardGroupBy,
  ): Promise<LeaderboardEntry[]> {
    await this.db.init();
    const userIds = await this.resolveScopeUserIds(filters, includeAllUsers);
    const interactionScope = this.scopeClause(userIds, 'created_by');
    const leadScope = this.scopeClause(userIds, 'created_by');
    const leadPlaceholders = LEAD_PRODUCING_OUTCOMES.map(() => '?').join(', ');

    const [doorRows, leadRows, valueRows] = await Promise.all([
      this.db.exec(
        `SELECT created_by AS user_id, COUNT(*) AS n FROM interactions
         WHERE deleted_at IS NULL AND occurred_at BETWEEN ? AND ?${interactionScope.sql} GROUP BY created_by`,
        [filters.from, filters.to, ...interactionScope.params],
      ) as Promise<Array<{ user_id: string; n: number }>>,
      this.db.exec(
        `SELECT created_by AS user_id, COUNT(*) AS n FROM interactions
         WHERE deleted_at IS NULL AND outcome_kind IN (${leadPlaceholders}) AND occurred_at BETWEEN ? AND ?${interactionScope.sql} GROUP BY created_by`,
        [...LEAD_PRODUCING_OUTCOMES, filters.from, filters.to, ...interactionScope.params],
      ) as Promise<Array<{ user_id: string; n: number }>>,
      this.db.exec(
        `SELECT created_by AS user_id, COALESCE(SUM(estimated_value), 0) AS n FROM leads
         WHERE deleted_at IS NULL AND created_at BETWEEN ? AND ?${leadScope.sql} GROUP BY created_by`,
        [filters.from, filters.to, ...leadScope.params],
      ) as Promise<Array<{ user_id: string; n: number }>>,
    ]);

    const doorsByUser = new Map(doorRows.map((r) => [r.user_id, r.n]));
    const leadsByUser = new Map(leadRows.map((r) => [r.user_id, r.n]));
    const valueByUser = new Map(valueRows.map((r) => [r.user_id, r.n]));
    const allUserIds = new Set([...doorsByUser.keys(), ...leadsByUser.keys(), ...valueByUser.keys()]);

    let entries: LeaderboardEntry[];
    if (groupBy === 'user') {
      const nameById = await this.userNameMap();
      entries = [...allUserIds].map((userId) => ({
        rank: 0,
        groupKey: userId,
        groupLabel: nameById.get(userId) ?? userId,
        doors: doorsByUser.get(userId) ?? 0,
        leads: leadsByUser.get(userId) ?? 0,
        estimatedValue: valueByUser.get(userId) ?? 0,
      }));
    } else {
      const teamsByUser = await this.teamsByUserMap();
      const byTeam = new Map<string, LeaderboardEntry>();
      for (const userId of allUserIds) {
        for (const team of teamsByUser.get(userId) ?? []) {
          const existing = byTeam.get(team.id) ?? { rank: 0, groupKey: team.id, groupLabel: team.name, doors: 0, leads: 0, estimatedValue: 0 };
          existing.doors += doorsByUser.get(userId) ?? 0;
          existing.leads += leadsByUser.get(userId) ?? 0;
          existing.estimatedValue += valueByUser.get(userId) ?? 0;
          byTeam.set(team.id, existing);
        }
      }
      entries = [...byTeam.values()];
    }

    entries.sort((a, b) => b[metric] - a[metric]);
    entries.forEach((e, i) => (e.rank = i + 1));
    return entries;
  }

  // ---------- shared helpers ----------

  /**
   * Resolves who a report's rows may be scoped to. `null` means "no restriction" (the
   * viewer has `interaction.read-others` and picked neither a specific user nor team).
   * An empty array means "nothing visible" (e.g. filtered to a user/team that isn't the
   * viewer, without `interaction.read-others`) -- deliberately distinct from `null` so
   * `scopeClause` can render a false-y `1 = 0` instead of accidentally matching everyone.
   */
  private async resolveScopeUserIds(filters: ReportFilters, includeAllUsers: boolean): Promise<string[] | null> {
    const selfId = this.authService.getCurrentUserId() ?? '';
    if (filters.teamId) {
      const members = await this.teamService.getMembers(filters.teamId);
      const memberIds = members.map((m) => m.user_id);
      return includeAllUsers ? memberIds : memberIds.filter((id) => id === selfId);
    }
    if (filters.userId) {
      return includeAllUsers || filters.userId === selfId ? [filters.userId] : [];
    }
    return includeAllUsers ? null : [selfId];
  }

  private scopeClause(userIds: string[] | null, column: string): ScopeClause {
    if (userIds === null) return { sql: '', params: [] };
    if (userIds.length === 0) return { sql: ' AND 1 = 0', params: [] };
    return { sql: ` AND ${column} IN (${userIds.map(() => '?').join(', ')})`, params: userIds };
  }

  private tagClause(tagId: string | undefined, joinTable: string, fkColumn: string, idExpr: string): ScopeClause {
    if (!tagId) return { sql: '', params: [] };
    return {
      sql: ` AND EXISTS (SELECT 1 FROM ${joinTable} jt WHERE jt.${fkColumn} = ${idExpr} AND jt.tag_id = ? AND jt.deleted_at IS NULL)`,
      params: [tagId],
    };
  }

  private outcomeClause(outcomeKind: OutcomeKind | undefined, column: string): ScopeClause {
    if (!outcomeKind) return { sql: '', params: [] };
    return { sql: ` AND ${column} = ?`, params: [outcomeKind] };
  }

  private bucketExpr(bucket: ReportBucket, column: string): string {
    const format = bucket === 'day' ? '%Y-%m-%d' : bucket === 'week' ? '%Y-W%W' : '%Y-%m';
    return `strftime('${format}', ${column} / 1000, 'unixepoch')`;
  }

  private async countOne(sql: string, params: unknown[]): Promise<number> {
    const rows = (await this.db.exec(sql, params)) as Array<{ n: number }>;
    return rows[0]?.n ?? 0;
  }

  private async userNameMap(): Promise<Map<string, string>> {
    const users = await this.userService.getAll(true);
    return new Map(users.map((u) => [u.id, u.full_name || u.email]));
  }

  private async teamsByUserMap(): Promise<Map<string, Array<{ id: string; name: string }>>> {
    const teams = await this.teamService.getAll();
    const membersByTeam = await Promise.all(teams.map((t) => this.teamService.getMembers(t.id)));
    const map = new Map<string, Array<{ id: string; name: string }>>();
    teams.forEach((team, idx) => {
      for (const member of membersByTeam[idx]) {
        const list = map.get(member.user_id) ?? [];
        list.push({ id: team.id, name: team.name });
        map.set(member.user_id, list);
      }
    });
    return map;
  }

  private async leadContactNameMap(leadIds: string[]): Promise<Map<string, string>> {
    if (leadIds.length === 0) return new Map();
    const placeholders = leadIds.map(() => '?').join(', ');
    const rows = (await this.db.exec(`SELECT id, contact_name FROM leads WHERE id IN (${placeholders})`, leadIds)) as Array<{
      id: string;
      contact_name: string;
    }>;
    return new Map(rows.map((r) => [r.id, r.contact_name]));
  }

  private async addressLabelMap(addressIds: string[]): Promise<Map<string, string>> {
    if (addressIds.length === 0) return new Map();
    const placeholders = addressIds.map(() => '?').join(', ');
    const rows = (await this.db.exec(`SELECT id, formatted_address FROM addresses WHERE id IN (${placeholders})`, addressIds)) as Array<{
      id: string;
      formatted_address: string;
    }>;
    return new Map(rows.map((r) => [r.id, r.formatted_address]));
  }

  private toRateTrendPoint(r: { bucket: string; group_key: string; doors: number; contacts: number; leads: number }, groupLabel: string): RateTrendPoint {
    return {
      bucketStart: 0, // display-only bucket label is authoritative; see bucketExpr()
      bucketLabel: r.bucket,
      groupKey: r.group_key,
      groupLabel,
      doors: r.doors,
      contacts: r.contacts,
      contactRate: r.doors > 0 ? r.contacts / r.doors : 0,
      leads: r.leads,
    };
  }

  private toLeadSummaryRow(r: LeadSummaryQueryRow, groupLabel: string): LeadSummaryRow {
    return {
      bucketStart: 0,
      bucketLabel: r.bucket,
      groupKey: r.group_key,
      groupLabel,
      leadCount: r.lead_count,
      totalValue: r.total_value,
      wonCount: r.won_count,
      lostCount: r.lost_count,
    };
  }
}

interface LeadSummaryQueryRow {
  bucket: string;
  group_key: string;
  lead_count: number;
  total_value: number;
  won_count: number;
  lost_count: number;
}
